import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
// eslint-disable-next-line
import daypilot from '../static/plugins/daypilot/daypilot-all.min';
// eslint-disable-next-line
import monthGreen from '../static/plugins/daypilot/month_green.css';

/* global DayPilot */

const propTypes = {
  fetchVisits: PropTypes.func.isRequired,
  showPatient: PropTypes.bool,
  showCaregiver: PropTypes.bool,
  addEventHandler: PropTypes.func,
  clickEventHandler: PropTypes.func,
  removeEventHandler: PropTypes.func,
};

const defaultProps = {
  showPatient: false,
  showCaregiver: true,
  addEventHandler: () => {},
  clickEventHandler: () => {},
  removeEventHandler: () => {},
};

@autobind
class Calendar extends BaseComponent {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.loadAndInitCalendar();
  }

  loadAndInitCalendar() {
    const style = document.createElement('link');
    style.type = 'text/css';
    style.rel = 'stylesheet';
    style.href = '/static/plugins/daypilot/month_green.css';
    style.async = true;
    document.head.appendChild(style);

    const script = document.createElement('script');
    style.type = 'text/javascript';
    script.src = '/static/plugins/daypilot/daypilot-all.min.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      this.initCalendar();
      this.initCalendarEvents();
    };
  }

  async initCalendar() {
    this.navigator = new DayPilot.Navigator('nav');
    this.navigator.showMonths = 3;
    this.navigator.selectMode = 'month';
    this.navigator.onTimeRangeSelected = async (args) => {
      this.monthCalendar.startDate = args.start;
      this.monthCalendar.update();
      this.refresh();
    };
    this.navigator.init();

    this.monthCalendar = new DayPilot.Month('dp');
    this.monthCalendar.viewType = 'Month';
    this.monthCalendar.theme = 'month_green';
    this.monthCalendar.eventHeight = 80;
    this.monthCalendar.init();
    // Fetch and show all events within the date range.
    this.refresh();
  }

  initCalendarEvents() {
    // On clicking a calender event.
    this.monthCalendar.onEventClicked = (args) => {
      // Trigger callback function.
      this.props.clickEventHandler(args.e);
    };
  }

  /* PUBLIC */
  async refresh() {
    this.clearEvents();

    // Fetch and show all events within the date range.
    const visits = await this.props.fetchVisits(
      this.monthCalendar.startDate.addDays(-6).toString('MM/dd/yyyy'),
      this.monthCalendar.startDate.addDays(37).toString('MM/dd/yyyy'),
    );

    // Add visits.
    visits.forEach((visit, index) => {
      this.addVisit(visit);
    });
  }

  addVisit(visit) {
    const invoiceLink = `<a target="_blank" href="/invoices/invoice_details?id=${visit.getLong('invoiceId')}">Y</a>`;
    const patientLink = `<a target="_blank" href="/patients/patient_profile?id=${visit.getLong('patientId')}">${visit.getString('patientName')}</a>`;
    const caregiverLink = `<a target="_blank" href="/caregivers/caregiver_profile?id=${visit.getLong('caregiverId')}">${visit.getString('caregiverName')}</a>`;

    // Create text displayed on the event.
    let text = visit.getString('dateUsString');
    text += ` B: ${(visit.getString('invoiceNumber') ? invoiceLink : 'N')}`;
    text += `<br>S: ${visit.getString('fromTimeString')}-${visit.getString('toTimeString')}`;
    if (!visit.getBoolean('missed')) {
      text += `<br>V: ${visit.getString('visitInString') || ''}-${visit.getString('visitOutString') || ''}`;
    } else {
      text += '<br>V: Missed';
    }
    if (this.props.showPatient) {
      text += visit.getLong('patientId') ? `<br>${patientLink}` : `<br>`;
    }
    if (this.props.showCaregiver) {
      text += visit.getLong('caregiverId') ? `<br>${caregiverLink}` : `<br>`;
    }

    // Create the event and add.
    const event = new DayPilot.Event({
      id: visit.getId(),
      start: new DayPilot.Date(visit.getString('dateString')),
      end: new DayPilot.Date(visit.getString('dateString')),
      text,
      resizeDisabled: true,
      cssClass: visit.getBoolean('missed') ? 'missed' : '',
      tags: {
        scheduleVisit: visit,
      },
    });
    this.addEvent(event);
  }

  addEvent(event) {
    this.monthCalendar.events.add(event);

    // Trigger addEventHandler callback function.
    this.props.addEventHandler(event);
  }

  removeEvent(event) {
    this.monthCalendar.events.remove(event);

    // Trigger callback function.
    this.props.removeEventHandler(event);
  }

  clearEvents() {
    if (this.monthCalendar.events.list) {
      const allEvents = this.monthCalendar.events.list.slice(); // Make a clone first.
      allEvents.forEach((event, index) => {
        this.removeEvent(event);
      });
    }
  }

  renderContent() {
    const { ...layoutProps } = this.props;

    return (
      <FlexRowContainer
        {...layoutProps}
      >
        <FlexColContainer
          width="150px"
          height="wrap_content"
        >
          <styledComp.Div
            id="nav"
            width="match_parent"
            height="wrap_content"
          />
        </FlexColContainer>

        <FlexColContainer
          width="0"
          height="wrap_content"
          flexGrow="1"
        >
          <styledComp.Div
            id="dp"
            width="match_parent"
            height="wrap_content"
          />
        </FlexColContainer>
      </FlexRowContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
Calendar.propTypes = propTypes;
Calendar.defaultProps = defaultProps;

export default Calendar;
